import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    H2Regular,
    H6Emphasized,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { MonospaceDetailText } from '../../common/components/GridCellRenderers';
import { Button } from '../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import { syncteraPersonUrl } from '../../common/utils/LinkUtils';
import { requestBuilder } from '../../http/RequestBuilder';
import { GenericDataTable } from './GenericDataTable';

interface CombinedStatementLog {
    id: string;
    yearMonth: string;
    syncteraPersonId: string;
    statementIds: string[];
    documentStatus: string;
    documentId: string | null;
    emailRequestType: string;
    emailStatus: string;
    emailAddress: string | null;
    emailType: string | null;
    startTime: Date | null;
    endTime: Date | null;
    notes: string | null;
    createdAt: Date;
    updatedAt: Date | null;
}
interface UnprocessedCombinedStatement {
    id: string;
    year_month: string;
    synctera_person_id: string;
    first_name: string | null;
    last_name: string | null;
    notes: string | null;
    statement_ids: string;
    document_status: string;
    email_request_type: string;
    email_status: string | null;
    startTime: Date | null;
    endTime: Date | null;
    created_at: Date;
    updated_at: Date | null;
}

const useGetUnprocessedStatements = requestBuilder.query<
    UnprocessedCombinedStatement[]
>({
    endpoint: `/api/admin/bank-ops/statement-log/unprocessed`,
    queryKey: `/api/admin/bank-ops/statement-log/unprocessed`,
});

interface StatementLogSummary {
    combinedStatementLogSummary: object[];
    statementCreatedLogSummary: object[];
}

const useGetStatementLogSummary = requestBuilder.query<StatementLogSummary>({
    endpoint: `/api/admin/bank-ops/statement-log/summary`,
    queryKey: `/api/admin/bank-ops/statement-log/summary`,
});

enum StatementEmailRequestType {
    DEFAULT = 'DEFAULT',
    NO_EMAIL = 'NO_EMAIL',
    NEW_COMBINED_STATEMENT = 'NEW_COMBINED_STATEMENT',
    COMBINED_STATEMENT_UPDATED = 'COMBINED_STATEMENT_UPDATED',
}

interface RetrySingleCombinedStatementRequestDto {
    id: string;
    overrideEmailRequestType?: StatementEmailRequestType;
}

export interface AdminBankOpsRetryCombinedStatementResponseDto {
    success: boolean;
    processedNewCombinedStatementLog: CombinedStatementLog;
}

const useRetrySingleCombinedStatement = requestBuilder.mutation<
    AdminBankOpsRetryCombinedStatementResponseDto,
    RetrySingleCombinedStatementRequestDto
>({
    endpoint: '/api/admin/bank-ops/statement/retry',
    method: 'POST',
});

export const BankOpsStatementsPanel = () => {
    const {
        data: unprocessedStatements,
        isLoading: isLoadingUnprocessedStatements,
        refetch: refectUnprocessedStatements,
        isRefetching: isRefetchingUnprocessedStatements,
    } = useGetUnprocessedStatements({});

    const {
        data: statementLogSummary,
        isLoading: isLoadingStatementLogSummary,
        refetch: refetchStatementLogSummary,
        isRefetching: isRefetchingStatementLogSummary,
    } = useGetStatementLogSummary({});

    if (
        isLoadingUnprocessedStatements ||
        isLoadingStatementLogSummary ||
        isRefetchingUnprocessedStatements ||
        isRefetchingStatementLogSummary
    ) {
        return <Spinner />;
    }

    return (
        <YStack gap={16} width="100%">
            <XStack justifyContent="space-between" width="100%">
                <H6Emphasized>Combined Statement Summary</H6Emphasized>
            </XStack>
            <GenericDataTable
                data={
                    statementLogSummary == null
                        ? null
                        : statementLogSummary.combinedStatementLogSummary
                }
            />

            <XStack justifyContent="space-between" width="100%">
                <H6Emphasized>Statement Created Log Summary</H6Emphasized>
            </XStack>
            <GenericDataTable
                data={
                    statementLogSummary == null
                        ? null
                        : statementLogSummary.statementCreatedLogSummary
                }
            />

            <XStack justifyContent="space-between" width="100%">
                <H6Emphasized>Unprocessed Combined Statements</H6Emphasized>
            </XStack>
            <UnprocessedStatementsTable
                unprocessedStatements={unprocessedStatements}
                refetchStatementData={() => {
                    void refetchStatementLogSummary();
                    void refectUnprocessedStatements();
                }}
            />
        </YStack>
    );
};

const UnprocessedStatementsTable = ({
    unprocessedStatements,
    refetchStatementData,
}: {
    unprocessedStatements?: UnprocessedCombinedStatement[] | null;
    refetchStatementData: () => void;
}) => {
    const [selectedUnprocessedStatement, setSelectedUnprocessedStatement] =
        useState<UnprocessedCombinedStatement | null>();
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
    const [retryResult, setRetryResult] =
        useState<AdminBankOpsRetryCombinedStatementResponseDto | null>();

    const { mutateAsync: retrySingleCombinedStatement } =
        useRetrySingleCombinedStatement();

    if (unprocessedStatements == null) {
        return null;
    }

    if (unprocessedStatements.length === 0) {
        return (
            <BodyMediumRegular>
                No unprocessed combined statements
            </BodyMediumRegular>
        );
    }

    const handleRetrySubmit = async (
        statementEmailRequestType: StatementEmailRequestType,
    ) => {
        setButtonDisabled(true);

        const retryResult = await retrySingleCombinedStatement({
            body: {
                id: (
                    selectedUnprocessedStatement as UnprocessedCombinedStatement
                ).id,
                overrideEmailRequestType: statementEmailRequestType,
            },
        });
        setRetryResult(retryResult);
    };

    const columnKeys = Object.keys(unprocessedStatements[0] as object);

    return (
        <>
            <YStack gap={8}>
                <DataTable
                    value={unprocessedStatements}
                    size="small"
                    tableStyle={{ width: '100%' }}
                    selectionMode="single"
                    selection={selectedUnprocessedStatement}
                    onSelectionChange={(e) => {
                        setSelectedUnprocessedStatement(
                            e.value as UnprocessedCombinedStatement,
                        );
                    }}
                    dataKey="id"
                    isDataSelectable={() => true}
                >
                    {columnKeys.map((key) => (
                        <Column key={key} field={key} header={key} />
                    ))}
                </DataTable>
            </YStack>
            <GroveSimpleModal
                isOpen={selectedUnprocessedStatement != null}
                onClose={() => {
                    setSelectedUnprocessedStatement(null);
                    setButtonDisabled(false);
                    if (retryResult != null) {
                        refetchStatementData();
                    }
                    setRetryResult(null);
                }}
            >
                {selectedUnprocessedStatement != null && (
                    <YStack
                        marginHorizontal={16}
                        marginTop={32}
                        width="90vw"
                        maxWidth={1000}
                    >
                        <H2Regular>Re-run Single Combined Statement</H2Regular>
                        <BodySmallRegular marginVertical={16}>
                            This should only be triggered by engineering.
                        </BodySmallRegular>
                        <div>
                            <a
                                href={syncteraPersonUrl(
                                    selectedUnprocessedStatement.synctera_person_id,
                                )}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Go to Synctera person
                            </a>
                        </div>
                        {retryResult != null && (
                            <div style={{ whiteSpace: 'pre' }}>
                                <MonospaceDetailText>
                                    {JSON.stringify(retryResult, null, 2)}
                                </MonospaceDetailText>
                            </div>
                        )}
                        <XStack justifyContent="center" gap={10}>
                            <Button
                                variant="primary"
                                width="auto"
                                size="small"
                                disabled={buttonDisabled}
                                onPress={() => {
                                    void handleRetrySubmit(
                                        StatementEmailRequestType.DEFAULT,
                                    );
                                }}
                            >
                                {buttonDisabled && retryResult != null
                                    ? 'Complete'
                                    : buttonDisabled
                                      ? 'Running....'
                                      : 'Re-run combined statement (default email handling)'}
                            </Button>

                            <Button
                                variant="primary"
                                width="auto"
                                size="small"
                                disabled={buttonDisabled}
                                onPress={() => {
                                    void handleRetrySubmit(
                                        StatementEmailRequestType.NO_EMAIL,
                                    );
                                }}
                            >
                                {buttonDisabled && retryResult != null
                                    ? 'Complete'
                                    : buttonDisabled
                                      ? 'Running....'
                                      : 'Re-run combined statement (no email)'}
                            </Button>
                        </XStack>
                    </YStack>
                )}
            </GroveSimpleModal>
        </>
    );
};
